import { createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "@/lib/firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  writeBatch,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  getDoc,
  updateDoc,
  deleteDoc,
  limit,
  startAfter,
  getDocs
} from "firebase/firestore";
import {
  setInventory,
  setInventoryLoading,
  appendInventory,
  setHasMore,
  setPaginationLoading
} from "./usePartsSlice";
import { deleteObject, getStorage, ref } from "firebase/storage";

const sortOrderMap = {
  premium: 2,
  pro: 3,
  free: 4
};

export const addPart = createAsyncThunk(
  "parts/addPart",
  async ({ currentPlan, user, formData, imageUrls }, { rejectWithValue }) => {
    console.log("formData in thunk: ", formData);
    try {
      const isMakeOther = formData.make === "Other";
      const isModelOther = formData.model === "Other";
      const isCategoryOther = formData.category === "Other";
      const isNameOther = formData.partName === "Other";

      const finalMake = isMakeOther ? formData.makeOther : formData.make;
      const finalModel = isModelOther ? formData.modelOther : formData.model;
      const finalPartCategory = isCategoryOther
        ? formData.partCategoryOther
        : formData.partCategory;
      const finalPartName = isNameOther
        ? formData?.partNameOther
        : formData?.partName;
      const stripePaymentLink =
        formData?.stripePaymentLink.trim() !== ""
          ? formData?.stripePaymentLink
          : "";
      const newPartData = {
        sellerId: user.uid,
        sellerName: user.companyName || user.name || "Unknown Seller",
        sellerLogo: user.logoUrl || null,
        isFounderMember: user?.isFounderMember ? true : false,
        year: parseInt(formData.year, 10),
        make: isMakeOther ? "other" : finalMake,
        model: isModelOther ? "other" : finalModel,
        category: isCategoryOther ? "Others" : finalPartCategory,
        categoryId: isCategoryOther
          ? formData.partCategoryId
          : formData.partCategoryId,
        name: isNameOther ? "other" : finalPartName,
        name_lowercase: (isNameOther
          ? finalPartName || finalPartCategory
          : finalPartName || finalPartCategory
        )?.toLowerCase(),

        description: formData.detailedDescription || null,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity, 10),
        postalCode: formData.postalCode,
        imageUrls,
        stripePaymentLink: stripePaymentLink,
        sortOrder: sortOrderMap[currentPlan] ?? 4,
        status:
          parseInt(formData.quantity, 10) > 5
            ? "Active"
            : parseInt(formData.quantity, 10) > 0
            ? "Low Stock"
            : "Out of Stock",
        dateAdded: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      if (isMakeOther) {
        newPartData.sub_make = finalMake;
      }
      if (isModelOther) {
        newPartData.sub_model = finalModel;
      }
      if (isCategoryOther) {
        newPartData.sub_category = finalPartCategory;
      }
      if (isNameOther) {
        newPartData.sub_name = finalPartName;
      }

      console.log("newPartData: ", newPartData);
      await addDoc(collection(db, "parts"), newPartData);
      return newPartData;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addPartsBulk = createAsyncThunk(
  "parts/addPartsBulk",
  async (
    { currentPlan, user, csvData, file, categories },
    { rejectWithValue }
  ) => {
    console.log("csvData: ", csvData);
    try {
      if (!csvData || csvData.length === 0) {
        throw new Error("CSV data is empty or invalid.");
      }

      if (!user || !user.uid) {
        throw new Error("User authentication required.");
      }

      const getCategoryIdByName = (categoryName) => {
        if (!categories) return null;

        const category = categories.find(
          (cat) => cat.name.toLowerCase() === categoryName?.toLowerCase()
        );
        if (category) {
          return category.id;
        }

        const otherCategory = categories.find(
          (cat) =>
            cat.name.toLowerCase() === "other" ||
            cat.name.toLowerCase() === "others"
        );
        return otherCategory?.id || null;
      };

      const partsCollection = collection(db, "parts");
      let batch = writeBatch(db);
      let counter = 0;
      let totalAdded = 0;
      let failedRows = [];

      for (let index = 0; index < csvData.length; index++) {
        try {
          const row = csvData[index];
          const rowNumber = index + 2;

          if (!row || Object.keys(row).length === 0) {
            continue;
          }

          // Determine if fields are "Other"
          const isMakeOther = row.make === "Other";
          const isModelOther = row.model === "Other";
          const isCategoryOther =
            row.partCategory?.toLowerCase() === "others" ||
            row.partCategory?.toLowerCase() === "other";
          const isNameOther = row.partName === "Other";

          const stripePaymentLink =
            row.stripePaymentLink && row.stripePaymentLink.trim() !== ""
              ? row.stripePaymentLink
              : "";

          // Get final values
          const finalMake = isMakeOther ? row.makeOther : row.make;
          const finalModel = isModelOther ? row.modelOther : row.model;
          const finalPartCategory = isCategoryOther
            ? row.partCategoryOther
            : row.partCategory;
          const finalPartName = isNameOther ? row.partNameOther : row.partName;

          // Validate required fields
          if (
            !row.year ||
            !finalMake ||
            !finalModel ||
            !finalPartCategory ||
            !finalPartName ||
            !row.price ||
            !row.quantity ||
            !row.postalCode
          ) {
            failedRows.push({
              row: rowNumber,
              reason: "Missing required fields",
              data: row
            });
            continue;
          }

          const year = parseInt(row.year, 10);
          const price = parseFloat(row.price);
          const quantity = parseInt(row.quantity, 10);

          if (isNaN(year) || isNaN(price) || isNaN(quantity)) {
            failedRows.push({
              row: rowNumber,
              reason: "Invalid numeric values",
              data: row
            });
            continue;
          }

          // Get category ID (handle "Others" category)
          const categoryId =
            row.partCategoryId || getCategoryIdByName(finalPartCategory);

          const newPartData = {
            sellerId: user.uid,
            sellerName: user.companyName || user.name || "Unknown Seller",
            sellerLogo: user.logoUrl || null,

            year: year,
            make: isMakeOther ? "other" : finalMake,
            makeId: row.makeId || "",
            model: isModelOther ? "other" : finalModel,
            category: isCategoryOther ? "Others" : finalPartCategory,
            categoryId: categoryId,
            name: isNameOther ? "other" : finalPartName,
            name_lowercase: (isNameOther
              ? finalPartName
              : finalPartName
            )?.toLowerCase(),

            description: row.detailedDescription?.trim() || null,
            price: price,
            quantity: quantity,
            postalCode: row.postalCode.trim(),
            imageUrls: [],
            stripePaymentLink: stripePaymentLink,
            status:
              quantity > 5
                ? "Active"
                : quantity > 0
                ? "Low Stock"
                : "Out of Stock",
            sortOrder: sortOrderMap[currentPlan] ?? 4,
            dateAdded: serverTimestamp(),
            lastUpdated: serverTimestamp(),

            uploadSource: "bulk_csv",
            uploadFileName: file.name,
            uploadTimestamp: serverTimestamp()
          };

          // Add sub_ fields only if "Other" is selected
          if (isMakeOther) {
            newPartData.sub_make = finalMake;
          }
          if (isModelOther) {
            newPartData.sub_model = finalModel;
          }
          if (isCategoryOther) {
            newPartData.sub_category = finalPartCategory;
          }
          if (isNameOther) {
            newPartData.sub_name = finalPartName;
          }

          const docRef = doc(partsCollection);
          batch.set(docRef, newPartData);
          counter++;
          totalAdded++;

          // Commit batch every 500 documents
          if (counter === 500) {
            await batch.commit();
            console.log(
              `Committed batch of ${counter} parts. Total processed: ${totalAdded}`
            );
            batch = writeBatch(db);
            counter = 0;
          }
        } catch (rowError) {
          console.error(`Error processing row ${index + 2}:`, rowError);
          failedRows.push({
            row: index + 2,
            reason: rowError.message,
            data: csvData[index]
          });
        }
      }

      // Commit remaining documents
      if (counter > 0) {
        await batch.commit();
        console.log(
          `Committed final batch of ${counter} parts. Total added: ${totalAdded}`
        );
      }

      if (failedRows.length > 0) {
        console.warn(
          `Failed to process ${failedRows.length} rows:`,
          failedRows
        );
      }

      const result = {
        count: totalAdded,
        failed: failedRows.length,
        failedRows: failedRows.slice(0, 10),
        fileName: file.name,
        totalRows: csvData.length
      };

      console.log("Bulk upload completed:", result);
      return result;
    } catch (err) {
      console.error("Bulk upload error:", err);
      return rejectWithValue(err.message || "Failed to upload parts");
    }
  }
);

export const updatePart = createAsyncThunk(
  "parts/updatePart",
  async ({ partId, formData, imageUrls }, { rejectWithValue }) => {
    console.log("formData in thunk: ", formData);
    try {
      if (!partId) {
        throw new Error("Part ID is required for update");
      }

      const isMakeOther = formData.make === "Other";
      const isModelOther = formData.model === "Other";
      const isCategoryOther = formData.partCategory === "other";
      const isNameOther = formData.partName === "Other";

      const finalMake = isMakeOther ? formData.makeOther : formData.make;
      const finalModel = isModelOther ? formData.modelOther : formData.model;
      const finalPartCategory = isCategoryOther
        ? formData.partCategoryOther
        : formData.partCategory;
      const stripePaymentLink =
        formData?.stripePaymentLink.trim() !== ""
          ? formData?.stripePaymentLink
          : "";
      const finalPartName = isNameOther
        ? formData?.partNameOther
        : formData?.partName;

      const updatedData = {
        year: parseInt(formData.year, 10),
        make: isMakeOther ? "other" : finalMake,
        model: isModelOther ? "other" : finalModel,
        category: isCategoryOther ? "Others" : finalPartCategory,
        categoryId: isCategoryOther
          ? formData.partCategoryId
          : formData.partCategoryId,
        name: isNameOther ? "other" : finalPartName,
        name_lowercase: (isNameOther
          ? finalPartName || finalPartCategory
          : finalPartName || finalPartCategory
        )?.toLowerCase(),

        description: formData.detailedDescription || null,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity, 10),
        postalCode: formData.postalCode,
        imageUrls: imageUrls || [],
        stripePaymentLink: stripePaymentLink,
        status:
          parseInt(formData.quantity, 10) > 5
            ? "Active"
            : parseInt(formData.quantity, 10) > 0
            ? "Low Stock"
            : "Out of Stock",
        lastUpdated: serverTimestamp()
      };

      if (isMakeOther) {
        updatedData.sub_make = finalMake;
      } else {
        updatedData.sub_make = null;
      }

      if (isModelOther) {
        updatedData.sub_model = finalModel;
      } else {
        updatedData.sub_model = null;
      }

      if (isCategoryOther) {
        updatedData.sub_category = finalPartCategory;
      } else {
        updatedData.sub_category = null;
      }

      if (isNameOther) {
        updatedData.sub_name = finalPartName;
      } else {
        updatedData.sub_name = null;
      }

      console.log("updatedData: ", updatedData);
      const partDocRef = doc(db, "parts", partId);
      await updateDoc(partDocRef, updatedData);

      return { id: partId, ...updatedData };
    } catch (err) {
      console.error("Error updating part:", err);
      return rejectWithValue(err.message || "Failed to update part");
    }
  }
);

export const fetchSellerInventory = createAsyncThunk(
  "parts/fetchSellerInventory",
  async (
    { sellerId, loadMore = false, pageSize = 10 },
    { getState, rejectWithValue }
  ) => {
    try {
      if (!sellerId) {
        throw new Error("Seller ID is required");
      }
      const state = getState();
      const { lastDoc } = state.useParts;

      const partsCollection = collection(db, "parts");
      let q = query(
        partsCollection,
        where("sellerId", "==", sellerId),
        orderBy("sortOrder", "asc"),
        orderBy("dateAdded", "desc"),
        limit(pageSize)
      );

      if (loadMore && lastDoc) {
        q = query(
          partsCollection,
          where("sellerId", "==", sellerId),
          orderBy("sortOrder", "asc"),
          orderBy("dateAdded", "desc"),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }

      const snapshot = await getDocs(q);
      const parts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        dateAdded: doc.data().dateAdded?.toDate?.() || doc.data().dateAdded
      }));

      const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === pageSize;

      return {
        parts,
        lastDoc: newLastDoc,
        hasMore,
        isInitial: !loadMore
      };
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error fetching seller inventory. Please contact support.";

      console.error("Error fetching seller inventory:", error);

      toast({
        title: "Fetching Error",
        description: message,
        variant: "destructive"
      });

      return rejectWithValue(message);
    }
  }
);

export const fetchFeaturedInventory = createAsyncThunk(
  "parts/fetchFeaturedInventory",
  async (
    { sellerId, loadMore = false, pageSize = 10 },
    { getState, rejectWithValue }
  ) => {
    try {
      if (!sellerId) {
        throw new Error("Seller ID is required");
      }

      const state = getState();
      const { featuredLastDoc } = state.useParts;

      const partsCollection = collection(db, "parts");
      let q = query(
        partsCollection,
        where("sellerId", "==", sellerId),
        where("isFeatured", "==", true),
        orderBy("lastUpdated", "desc"),
        limit(pageSize)
      );

      if (loadMore && featuredLastDoc) {
        q = query(
          partsCollection,
          where("sellerId", "==", sellerId),
          where("isFeatured", "==", true),
          orderBy("lastUpdated", "desc"),
          startAfter(featuredLastDoc),
          limit(pageSize)
        );
      }

      const snapshot = await getDocs(q);
      const featuredParts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        dateAdded: doc.data().dateAdded?.toDate?.() || doc.data().dateAdded
      }));

      const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === pageSize;

      return {
        featuredParts,
        lastDoc: newLastDoc,
        hasMore,
        isInitial: !loadMore
      };
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error fetching featured inventory. Please contact support.";

      console.error("Error fetching featured inventory:", error);

      toast({
        title: "Fetching Error",
        description: message,
        variant: "destructive"
      });

      return rejectWithValue(message);
    }
  }
);

export const listenToSellerInventory = (sellerId) => (dispatch) => {
  if (!sellerId) {
    dispatch(setInventory([]));
    dispatch(setInventoryLoading(false));
    return;
  }

  dispatch(setInventoryLoading(true));

  const partsCollection = collection(db, "parts");
  const q = query(
    partsCollection,
    where("sellerId", "==", sellerId),
    orderBy("sortOrder", "asc"),
    orderBy("dateAdded", "desc")
  );

  let unsubscribeInventory = onSnapshot(
    q,
    (snapshot) => {
      const parts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        dateAdded: doc.data().dateAdded?.toDate?.() || doc.data().dateAdded
      }));

      dispatch(setInventory(parts));
      dispatch(setInventoryLoading(false));
    },
    (error) => {
      console.error("Error listening to inventory:", error);
      dispatch(setInventoryLoading(false));
    }
  );
};

export const fetchPartById = createAsyncThunk(
  "parts/fetchPartById",
  async (itemId, { rejectWithValue }) => {
    try {
      if (!itemId) {
        throw new Error("Item ID is required");
      }

      const docRef = doc(db, "parts", itemId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Part not found");
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Error fetching part by ID:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const deletePart = createAsyncThunk(
  "parts/deletePart",
  async (docId, { rejectWithValue }) => {
    try {
      if (!docId) {
        throw new Error("Document ID is required");
      }

      const docRef = doc(db, "parts", docId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Document not found");
      }

      const data = docSnap.data();
      const storage = getStorage();

      if (Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        await Promise.all(
          data.imageUrls.map(async (url) => {
            try {
              const fileRef = ref(storage, url);
              console.log("fileRef: ", fileRef);
              await deleteObject(fileRef);
            } catch (err) {
              console.warn("Failed to delete image:", url, err);
            }
          })
        );
      }

      await deleteDoc(docRef);

      return { id: docId };
    } catch (error) {
      console.error("Error deleting part:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPartCategories = createAsyncThunk(
  "useParts/fetchPartCategories",
  async (_, { rejectWithValue }) => {
    try {
      const partCategoriesCollection = collection(db, "partCategories");

      const q = query(partCategoriesCollection, orderBy("totalParts", "desc"));

      const querySnapshot = await getDocs(q);

      const categories = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(Boolean);

      return categories;
    } catch (error) {
      console.error("Error fetching part categories:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateFeaturePart = createAsyncThunk(
  "parts/updateFeaturePart",
  async ({ partId }, { rejectWithValue }) => {
    try {
      if (!partId) {
        throw new Error("Part ID is required to update feature");
      }

      const partDocRef = doc(db, "parts", partId);

      await updateDoc(partDocRef, {
        isFeatured: true,
        sortOrder: 1,
        lastUpdated: serverTimestamp()
      });

      return { id: partId, isFeatured: true, sortOrder: 1 };
    } catch (error) {
      console.error("Error updating feature part:", error);
      return rejectWithValue(error.message || "Failed to feature part");
    }
  }
);
